// eslint-disable-next-line no-restricted-imports -- Like auth/store useIsViewOnly, this billing hook bridges imperative mutation guards and reactive editor access, including expiry.
import { useEffect, useState } from "react";
import { useAuthStore } from "../auth/store";
import { useProjectStore } from "../projects/store";
import { getSubscriptionPilot, useSubscriptionPilot } from "../subscription-pilot";
import { currentInstallationPack, hasSubscriptionAccess, passAccess, useBillingStore } from "./store";

export const SUBSCRIPTION_EDIT_REQUESTED = "crimp:subscription-edit-requested";

/** Applies to editing only. Reading a public example or an expired project
 * never needs a subscription, and previous purchased rights remain valid. */
export function subscriptionEditingBlocked(): boolean {
  const auth = useAuthStore.getState();
  if (auth.isPublicView) return true;
  if (auth.adminReady && auth.isAdmin) return false;
  const billing = useBillingStore.getState();
  const lifetime = billing.entitlementSource === "server" && billing.entitlement?.userId === auth.user?.id && billing.entitlement?.lifetime;
  // A pilot visitor may buy a Pass once their subscription has ended; that
  // Pass unlocks editing exactly like the subscription did, until it expires.
  if (lifetime || passAccess() === "active" || hasSubscriptionAccess() || currentInstallationPack()) return false;
  const pilot = getSubscriptionPilot();
  return !pilot.ready || pilot.assigned;
}

export function requestSubscriptionEditing(): boolean {
  if (!subscriptionEditingBlocked()) return true;
  if (typeof window !== "undefined") window.dispatchEvent(new Event(SUBSCRIPTION_EDIT_REQUESTED));
  return false;
}

/** Re-renders the caller the moment the signed-in account's Pass ends while
 * the editor is open, so paid-access reads flip without a reload. The
 * timeout is clamped to the browser's 32-bit limit like the subscription one. */
export function usePaidAccessTick(): void {
  const entitlement = useBillingStore((s) => s.entitlement);
  const [clock, tick] = useState(0);
  useEffect(() => {
    const ends = entitlement?.passExpiresAt ? Date.parse(entitlement.passExpiresAt) : NaN;
    if (!Number.isFinite(ends)) return;
    const remaining = ends - Date.now();
    if (remaining <= 0) return;
    const timer = window.setTimeout(() => tick((n) => n + 1), Math.min(remaining + 1, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [entitlement, clock]);
}

/** Expiration and account changes update every editor surface in this tab. */
export function useSubscriptionEditingBlocked(): boolean {
  useSubscriptionPilot();
  usePaidAccessTick();
  useAuthStore((s) => s.user);
  useAuthStore((s) => s.isAdmin);
  useAuthStore((s) => s.adminReady);
  useAuthStore((s) => s.isPublicView);
  useProjectStore((s) => s.activeLocalProjectId);
  useBillingStore((s) => s.configReady);
  useBillingStore((s) => s.config);
  useBillingStore((s) => s.configUnavailable);
  useBillingStore((s) => s.entitlement);
  useBillingStore((s) => s.entitlementSource);
  useBillingStore((s) => s.installationPacks);
  useBillingStore((s) => s.installationPacksSource);
  const subscription = useBillingStore((s) => s.subscription);
  useBillingStore((s) => s.subscriptionSource);
  const [clock, tick] = useState(0);
  useEffect(() => {
    if (!subscription?.currentPeriodEndsAt) return;
    const remaining = Date.parse(subscription.currentPeriodEndsAt) - Date.now();
    if (remaining <= 0) return;
    // Clamp long-lived monthly periods to the browser timer limit.
    const timer = window.setTimeout(() => tick((n) => n + 1), Math.min(remaining + 1, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [subscription, clock]);
  return subscriptionEditingBlocked();
}
